import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { handleDomBubbleEvent } from '@/utils/dom';
import { memo, useCallback, useMemo } from 'react';
import { Spinner } from '@/components/_ui/spinner';
import { AxiosErrorCustom, axiosExtractErrorMessage } from '@/lib/axios';
import { useSelectedCases, useSelectedMenuCaseMode, useCasesActions } from '../../../stores/cases.store';
import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import { useUnassignCases } from '../../../api/unassign-cases.api';
import useSelectedCaseQs from '../../../hooks/use-case-qs';

export type TCloseCaseModalType = 'CloseCaseModal';
export type ICloseCaseModalProps = {
  caseId?: string | null; // Optional: If provided, only close this conversation
};

const CloseCaseModal = memo(({ caseId }: ICloseCaseModalProps) => {
  // HOOKS
  const { handleSelectedCase } = useSelectedCaseQs();
  const showToast = useCustomToast();
  const selectedCases = useSelectedCases();
  const selectedCaseMode = useSelectedMenuCaseMode();
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
  const { setResetSelectedCases, setResetSelectedMenuCaseMode, setResetSelectedCase } = useCasesActions();

  const isOpenCloseCaseModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('CloseCaseModal');
  }, [caseModuleModalRootActions]);

  const mutationCloseConversation = useUnassignCases({
    mutationConfig: {
      onSuccess: () => {
        showToast({
          type: 'success',
          message: 'Conversation closed successfully',
        });
        caseModuleModalRootActions.closeModal('CloseCaseModal');
        handleSelectedCase(null);
        setResetSelectedCases();
        setResetSelectedCase();
        if (selectedCaseMode !== 'idle') {
          setResetSelectedMenuCaseMode();
        }
      },
      onError: (error: Error) => {
        showToast({
          type: 'error',
          message: axiosExtractErrorMessage(error as AxiosErrorCustom) || 'Failed to close conversation',
        });
        caseModuleModalRootActions.closeModal('CloseCaseModal');
      },
    },
  });

  // EVENT HANDLERS
  const handleCloseModal = useCallback(() => {
    caseModuleModalRootActions.closeModal('CloseCaseModal');
    if (selectedCaseMode === 'idle') {
      setResetSelectedCases();
    }
  }, [caseModuleModalRootActions, selectedCaseMode, setResetSelectedCases]);

  const handleSubmit = useCallback(() => {
    const caseIdsFromStore = selectedCases?.map((item) => item?.caseId) || [];
    const caseIds = caseId ? [caseId] : caseIdsFromStore;

    if (caseIds?.length === 0) {
      showToast({
        type: 'error',
        message: 'Please select at least one case',
      });
      return;
    }
    mutationCloseConversation.mutate({
      caseIds,
    });
  }, [caseId, selectedCases, showToast, mutationCloseConversation]);

  const displayFormDesc = useMemo(() => {
    if (selectedCases?.length === 1) {
      return (
        <span className="text-[14px] font-normal text-[#9D9D9C]">Would you like to close this conversation ?</span>
      );
    }
    return (
      <span className="text-[14px] font-normal text-[#9D9D9C]">
        Are you sure you want to unassign{' '}
        <span className="font-bold text-[#9D9D9C] px-[2px]">{selectedCases?.length} cases? </span>
        <br /> Once unassign, this conversation will end. Future messages will start a new conversation.
      </span>
    );
  }, [selectedCases?.length]);

  // EARLY RETURN
  if (!isOpenCloseCaseModal) {
    return null;
  }

  // RENDER LOGIC
  return (
    <Modal
      isCloseOutside={false}
      isButtonClose={true}
      onClose={handleCloseModal}
      className="w-[500px] p-[24px]"
      isOpen={isOpenCloseCaseModal}
    >
      <ModalHeader>
        <ModalTitle>Unassign Case</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="grid gap-[16px]">{displayFormDesc}</div>
      </ModalContent>
      <ModalFooter className="gap-[12px]">
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleCloseModal();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton onClick={handleSubmit} disabled={mutationCloseConversation.isPending}>
          {mutationCloseConversation.isPending ? <Spinner className="w-[18px] h-[18px]" /> : null} Oke
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

CloseCaseModal.displayName = 'CloseCaseModal';

export default CloseCaseModal;
